import React, { useEffect, useState } from "react";
import RecoveryNumberView from "../components/recovery-number-view";
import { stopEvent, getAPIError, getSafeReferrer, getErrorCategory } from "lib/utility-functions";
import {
  recoveryPhoneNumberGet,
  recoveryPhoneNumberSendCode,
  recoveryPhoneNumberPatch,
  recoveryPhoneNumberVerifyCode,
  recoveryPhoneNumberResendCode,
  recoveryPhoneNumberDelete,
  isLoading,
  getPhoneNumber,
  getMobileNumberIsLoading
} from "modules/recovery-phone-number-module";
import { meLocale, meData } from "modules/me-module";
import { useDispatch, useSelector } from "react-redux";
import * as Castle from "@castleio/castle-js";
import { ensureCastleConfigured } from "lib/castleio-tracking";
import auth from "lib/profile-auth";
import { emailVerificationRequestPost } from "modules/identity-module";
import { uiShowToastrMessage } from "modules/ui-module";
import { setHandledErrorCodes, removeHandledErrorCodes } from 'lib/error-message-handling';
import { identityEmailToken } from "modules/identity-module";
import useAmplitudePageView from "modules/tracking/amplitude-module";
import {
  recoveryMobileNumberSetupStarted,
  recoveryMobileNumberSetupSuccessful,
  recoveryMobileNumberSetupFailed,
  recoveryMobileNumberCreationStarted,
  recoveryMobileNumberEditStarted,
  recoveryMobileNumberEditSuccessful,
  recoveryMobileNumberEditFailed,
  recoveryMobileNumberDeleted,
  recoveryMobileNumberDeletionStarted,
  recoveryMobileNumberDeletionFailed,
  recoveryMobileNumberVerificationFailed,
  recoveryMobileNumberSendCodeFailed,
  recoveryMobileNumberResendCodeFailed,
} from "modules/tracking/tracking-constants";

const getErrorCodesArray = () => {
  return [
    400,
    404,
    409,
    429,
    'invalid.code',
    'verification.not.found',
    'missing.castleRequestToken',
    'castle.denied',
    'user.suspended',
  ];
};

export const RecoveryNumberContainer = () => {
  const [editPhoneNumberClick, setEditPhoneNumberClick] = useState(false);
  const [isPhoneNumberSetupAlready, setIsPhoneNumberSetupAlready] = useState(false);
  const [phoneNumber, setPhoneNumber] = useState("");
  const {
    meLocale,
    meEmail,
    isloading,
    recoveryPhoneNumber,
    getMobileNumberIsLoading,
    emailToken
  } = useSelector(getValuesFromStore);
  const [modalState, setModalState] = useState("");
  const [id,setId] = useState("");
  const [timer, setTimer] = useState(0);
  const [otp,setOtp] = useState("");
  const [disableVerifyCode,setDisableVerifyCode] = useState(true);
  const [disableSendCode,setDisableSendCode] = useState(true);
  const [isOpenVerificationModal, setIsOpenVerificationModal] = useState(false);
  const [error, setError] = useState("");
  const dispatch = useDispatch();
  const track = useAmplitudePageView();
  const timerIntervalRef = React.useRef(null);
  const [maskedNumber ,setMaskedNumber] = useState("");

  const handleMaskedNumber = (number) => {
    const phoneNumber = String(number || '');
    const digits = phoneNumber.replace(/\D/g, '');
    const maskedDigits = '*'.repeat(Math.max(0, digits.length - 3));
    const lastThreeDigits = digits.slice(-3);
    setMaskedNumber(maskedDigits + lastThreeDigits);
  };

  const handleEditPhoneNumberClick = () => {
    track(recoveryMobileNumberEditStarted, {
      hasMobileNumber: !!recoveryPhoneNumber,
      referrer: getSafeReferrer()
    });

    setEditPhoneNumberClick(true);
    const minLoa = auth.token?.loa || 2;
    if (minLoa < 3) {
      setModalState("EmailVerification");
      setIsOpenVerificationModal(true);
      sendVerificationEmail();
    }
    else {
      setModalState("AddRecoveryPhoneNumber");
    }
  };

  const onChangePhoneNumber = (value) => {
    setError("");
    const formatted = value ? `+${value}` : "";
    setPhoneNumber(formatted);
    if(formatted.length <= 0 || formatted === recoveryPhoneNumber) {
      setDisableSendCode(true);
    }
    else {
      setDisableSendCode(false);
    }
  };

  const closeModal = () => {
    setEditPhoneNumberClick(false);
    setModalState("");
    setError("");
    const number = recoveryPhoneNumber || "";
    setPhoneNumber(number);
    handleMaskedNumber(number);
  };

  const handleResendCode = (e) => {
    stopEvent(e);
    setCountDown(30);
    setHandledErrorCodes(getErrorCodesArray());
    dispatch(recoveryPhoneNumberResendCode(id)).then(
      () => {
        removeHandledErrorCodes(getErrorCodesArray());
      },
      (error) => {
        removeHandledErrorCodes(getErrorCodesArray());
        const errorObj = getAPIError(error);
        const errorCode = errorObj && errorObj.length === 1 ? errorObj[0].code : '';

        track(recoveryMobileNumberResendCodeFailed, {
          hasMobileNumber: !!phoneNumber,
          errorCode: errorCode,
          errorCategory: getErrorCategory(errorCode, error?.status),
          statusCode: error?.status
        });

        if(errorCode === "verification.not.found") {
          displayToastrMessage(
            "error",
            "recovery.phone-number.verification.id.expired"
          );
          closeModal();
        }
        if(errorCode === "rate.limit.exceeded" || error?.status === 429) {
          displayToastrMessage(
            "error",
            "recovery.phone-number.resend-code.rate-limit-exceeded"
          );
          closeModal();
        }
      }
    );
  };

  const onDeletePhoneNumber = (e) => {
    const authToken = `Email ${emailToken}`;
    setHandledErrorCodes(getErrorCodesArray());
    dispatch(recoveryPhoneNumberDelete(authToken)).then(
      (response) => {
        removeHandledErrorCodes(getErrorCodesArray());
        setPhoneNumber("");
        handleMaskedNumber("");
        setIsPhoneNumberSetupAlready(false);
        setModalState("PhoneNumberDeleted");

        track(recoveryMobileNumberDeleted, {
          hasMobileNumber: !!recoveryPhoneNumber,
          referrer: getSafeReferrer()
        });
      },
      (error) => {
        removeHandledErrorCodes(getErrorCodesArray());
        const errorObj = getAPIError(error);
        const errorCode = errorObj && errorObj.length === 1 ? errorObj[0].code : '';

        track(recoveryMobileNumberDeletionFailed, {
          hasMobileNumber: !!recoveryPhoneNumber,
          errorCode: errorCode,
          errorCategory: getErrorCategory(errorCode)
        });

        displayToastrMessage(
          "error",
          "recovery.phone-number.deletion.failed"
        );
        closeModal();
      }
    );
  };

  const setCountDown = (seconds) => {
    if (timerIntervalRef.current) {
      clearInterval(timerIntervalRef.current);
    }

    setTimer(seconds);
    timerIntervalRef.current = setInterval(() => {
      setTimer((prevTimer) => {
      if (prevTimer <= 1) {
        clearInterval(timerIntervalRef.current);
        timerIntervalRef.current = null;
        return 0;
      }
      return prevTimer - 1;
      });
    }, 1000);
  };

  const handleModalCTAClick = (e, buttonId) => {
    stopEvent(e);
    const cta = e.target.id;

    switch (buttonId || cta) {
      case "send-verification-code":
        setHandledErrorCodes(getErrorCodesArray());
        // The start-verification call is Castle-gated - a fresh request token is required on every attempt.
        // Configure defensively rather than relying on another container (e.g., DevicesEditorContainer)
        // having already set up the Castle SDK on this page.
        ensureCastleConfigured();
        Castle.createRequestToken().then((castleRequestToken) => {
          const payload = {
            mobileNumber: phoneNumber,
            locale: meLocale,
            castleRequestToken,
          };
          // Editing an existing number at LOA < 3 requires the email verification token as step-up substitute
          const minLoa = auth.token?.loa || 2;
          const authProp = isPhoneNumberSetupAlready && minLoa < 3 && emailToken ? `Email ${emailToken}` : undefined;
          return dispatch(recoveryPhoneNumberSendCode(payload, authProp)).then(
            (response) => {
              removeHandledErrorCodes(getErrorCodesArray());
              track(recoveryMobileNumberSetupStarted, {
                hasMobileNumber: !!phoneNumber,
                isEdit: editPhoneNumberClick,
                referrer: getSafeReferrer()
              });

              setId(response?.payload?.id);
              setModalState("VerifyRecoveryPhoneNumber");
              setCountDown(30);
            },
            (error) => {
              removeHandledErrorCodes(getErrorCodesArray());
              const errorObj = getAPIError(error);
              const errorCode = errorObj && errorObj.length === 1 ? errorObj[0].code : '';

              track(recoveryMobileNumberSendCodeFailed, {
                hasMobileNumber: !!phoneNumber,
                errorCode: errorCode,
                errorCategory: getErrorCategory(errorCode, error?.status),
                statusCode: error?.status,
                isEdit: editPhoneNumberClick
              });

              if(errorCode === "invalid.mobileNumber") {
                setError("invalid-phone-number");
              }
              else if(errorCode === "missing.castleRequestToken" || errorCode === "castle.denied") {
                displayToastrMessage(
                  "error",
                  "recovery.phone-number.send-code.request-not-verified"
                );
                closeModal();
              }
              else if(errorCode === "user.suspended") {
                displayToastrMessage(
                  "error",
                  "recovery.phone-number.user-suspended"
                );
                closeModal();
              }
              else if(error?.status === 409) {
                displayToastrMessage(
                  "error",
                  "recovery.phone-number.send-code.conflict"
                );
                closeModal();
              }
              else if(errorCode === 'rate.limit.exceeded' || error?.status === 429) {
                displayToastrMessage(
                  "error",
                  "recovery.phone-number.send-code.rate-limit-exceeded"
                );
                closeModal();
              }
            }
          );
        },
        () => {
          removeHandledErrorCodes(getErrorCodesArray());
          track(recoveryMobileNumberSendCodeFailed, {
            hasMobileNumber: !!phoneNumber,
            errorCode: "castle.token.generation.failed",
            errorCategory: getErrorCategory("castle.token.generation.failed"),
            statusCode: undefined,
            isEdit: editPhoneNumberClick
          });

          displayToastrMessage(
            "error",
            "recovery.phone-number.send-code.request-not-verified"
          );
          closeModal();
        });
        break;
      case "back":
        setModalState("AddRecoveryPhoneNumber");
        break;
      case "verify-code":
        setHandledErrorCodes(getErrorCodesArray());
        dispatch(recoveryPhoneNumberVerifyCode(id, { code: otp }))
          .then(
            (response) => {
              setPhoneNumber(response?.payload?.mobileNumber);
              const minLoa = auth.token?.loa || 2;
              if(response?.payload?.status === "VERIFIED") {
                const authToken = `Mobile ${id}${minLoa < 3 ? `, Email ${emailToken}` : ''}`;
                // Keep the codes registered until the chained patch settles - it can fail with the
                // same handled statuses (e.g., mobile.not.verified 400) and must not hit the
                // interceptor's forced error page.
                dispatch(recoveryPhoneNumberPatch(authToken, { mobileNumber: phoneNumber }))
                  .then((response) => {
                    removeHandledErrorCodes(getErrorCodesArray());
                    const isEdit = editPhoneNumberClick;

                    if (isEdit) {
                      track(recoveryMobileNumberEditSuccessful, {
                        hadOldMobileNumber: !!recoveryPhoneNumber,
                        hasNewMobileNumber: !!phoneNumber,
                        status: 'verified',
                        referrer: getSafeReferrer()
                      });
                    } else {
                      track(recoveryMobileNumberSetupSuccessful, {
                        hasMobileNumber: !!phoneNumber,
                        status: 'verified',
                        referrer: getSafeReferrer()
                      });
                    }

                    setModalState("PhoneNumberVerified");
                    setIsPhoneNumberSetupAlready(true);
                  },
                  (error) => {
                    removeHandledErrorCodes(getErrorCodesArray());
                    const errorObj = getAPIError(error);
                    const errorCode = errorObj && errorObj.length === 1 ? errorObj[0].code : '';

                    if (editPhoneNumberClick) {
                      track(recoveryMobileNumberEditFailed, {
                        hadOldMobileNumber: !!recoveryPhoneNumber,
                        hasNewMobileNumber: !!phoneNumber,
                        errorCode: errorCode,
                        errorCategory: getErrorCategory(errorCode),
                        stage: 'patch'
                      });
                    } else {
                      track(recoveryMobileNumberSetupFailed, {
                        hasMobileNumber: !!phoneNumber,
                        errorCode: errorCode,
                        errorCategory: getErrorCategory(errorCode),
                        stage: 'patch'
                      });
                    }

                    if(errorCode === 'mobile.not.verified') {
                       displayToastrMessage(
                        "error",
                        "recovery.phone-number.verification.id.expired"
                      );
                    }
                  });
              }
              else {
                removeHandledErrorCodes(getErrorCodesArray());
              }
            },
            (error) => {
              removeHandledErrorCodes(getErrorCodesArray());
              const errorObj = getAPIError(error);
              const errorCode = errorObj && errorObj.length === 1 ? errorObj[0].code : '';

              track(recoveryMobileNumberVerificationFailed, {
                hasMobileNumber: !!phoneNumber,
                errorCode: errorCode,
                errorCategory: getErrorCategory(errorCode, error?.status),
                statusCode: error?.status,
                isEdit: editPhoneNumberClick
              });

              if( errorCode === "invalid.code") {
                setError("invalid-code");
              }
              if(errorCode === "verification.not.found") {
                displayToastrMessage(
                  "error",
                  "recovery.phone-number.verification.id.expired"
                );
                closeModal();
              }
              if(errorCode === "rate.limit.exceeded") {
                displayToastrMessage(
                  "error",
                  "recovery.phone-number.verify.rate-limit-exceeded"
                );
                closeModal();
              }
            }
          );
        break;
      case "cancel":
        closeModal();
        break;
      case "got-it":
        closeModal();
        break;
      case "delete-phone-number":
        track(recoveryMobileNumberDeletionStarted, {
          hasMobileNumber: !!recoveryPhoneNumber,
          referrer: getSafeReferrer()
        });
        setModalState("DeleteRecoveryPhoneNumber");
        break;
      case "delete-back":
        setModalState("AddRecoveryPhoneNumber");
        break;
      case "delete-confirm":
        onDeletePhoneNumber(e);
        break;
    }
  };

  const onBlur = (value) => {
    setError("");
    if(value.length === 6) {
      setDisableVerifyCode(false);
    }
  };

  const onComplete = (value) => {
    setOtp(value);
    setDisableVerifyCode(false);
  };

  const displayToastrMessage = (toastrType, toastrMessageId, toastrParams = null) => {
    dispatch(uiShowToastrMessage(toastrType, toastrMessageId, toastrParams));
  };

  const sendVerificationEmail = () => {
    dispatch(
      emailVerificationRequestPost(meLocale, {
        email: meEmail,
      })
    ).then(
      () => {
        displayToastrMessage(
          "success",
          "email-editor.verification-email-send-successful"
        );
      },
      () => {
        displayToastrMessage(
          "error",
          "email-editor.verification-email-send-failed"
        );
      }
    );
  };

  const closeEmailVerificationModal = () => {
    setIsOpenVerificationModal(false);
    setModalState("");
  };

  const completeProcess = ()=> {
    setIsOpenVerificationModal(false);
    setEditPhoneNumberClick(true);
    setModalState("AddRecoveryPhoneNumber");
  };

  useEffect(() => {
    dispatch(recoveryPhoneNumberGet()).then((response) => {
      const number = response?.payload?.mobileNumber || "";
      setPhoneNumber(number);
      handleMaskedNumber(number);
      setIsPhoneNumberSetupAlready(!!response?.payload?.mobileNumber);
    });

    if (timerIntervalRef.current) {
      clearInterval(timerIntervalRef.current);
    }

  }, [dispatch]);

  return (
    <RecoveryNumberView
      isPhoneNumberSetupAlready={isPhoneNumberSetupAlready}
      isLoading={isloading}
      getMobileNumberIsLoading={getMobileNumberIsLoading}
      onClickSetPhoneNumber={() => {
        track(recoveryMobileNumberCreationStarted, {
          referrer: getSafeReferrer()
        });
        setModalState("AddRecoveryPhoneNumber");
      }}
      editPhoneNumberClick={editPhoneNumberClick}
      maskedNumber={maskedNumber}
      phoneNumber={phoneNumber}
      closeModal={closeModal}
      onChangePhoneNumber={onChangePhoneNumber}
      modalState={modalState}
      onSave={handleModalCTAClick}
      timer={timer}
      onClickResendCode={handleResendCode}
      onBlur={onBlur}
      onComplete={onComplete}
      disableVerifyCode={disableVerifyCode}
      onClickEditPhoneNumber={handleEditPhoneNumberClick}
      disableSendCode={disableSendCode}
      closeEmailVerificationModal={closeEmailVerificationModal}
      completeProcess={completeProcess}
      sendVerificationEmail={sendVerificationEmail}
      isOpenVerificationModal={isOpenVerificationModal}
      error={error}
    />
  );
};

export const getValuesFromStore = (state) => {
  const mePersonData = meData(state);
  const emails = mePersonData.emails || [];
  const primaryEmail = emails.find((email) => email.type === "primary") || "";
  return {
    meLocale: meLocale(state),
    meEmail: primaryEmail.value,
    isloading: isLoading(state),
    recoveryPhoneNumber: getPhoneNumber(state),
    getMobileNumberIsLoading: getMobileNumberIsLoading(state),
    emailToken: identityEmailToken(state),
  };
};

export default RecoveryNumberContainer;
